/* This example requires Tailwind CSS v2.0+ */
import { useState } from "react";
import * as React from "react";
import { Notify } from "notiflix";
import { Link } from "gatsby";
import { IWPLink } from "@/src/types";
import axios from "axios";
import { useLocalization } from "../../../contexts/Localization";
import useFormData from "@/components/QuoteForm/useFormData";

export interface Props {
  title: string;
}

type FormData = {
  name: string;
  company: string;
  email: string;
  message: string;
};

type FormState = {
  isLoading: boolean;
  success: boolean;
  data: FormData;
};

const emptyForm = (): FormData => ({
  name: "",
  company: "",
  email: "",
  message: "",
});

const emptyFormState = (): FormState => ({
  isLoading: false,
  success: false,
  data: emptyForm(),
});

const ContactForm: React.FC<Props> = ({ title }) => {
  const [formState, setFormState] = useState(emptyFormState());

  const { language } = useLocalization();

  const formData = useFormData(language);
  const {
    recipient,
    nameLabel,
    companyLabel,
    emailLabel,
    messageLabel,
    submitLabel,
    successMessage,
    termsLink,
  } = formData;

  function updateFormProperty<K extends keyof FormData>(
    property: K,
    value: FormData[K]
  ) {
    setFormState((state) => ({
      ...state,
      data: {
        ...state.data,
        [property]: value,
      },
    }));
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    var html_message =
      "<h1>Nytt meddelande från hemsidan</h1> <br>" +
      "Email: " +
      formState.data.email +
      "<br>" +
      "Namn: " +
      formState.data.name +
      "<br>" +
      "Företag: " +
      formState.data.company +
      "<br>" +
      "Meddelande: " +
      formState.data.message +
      "<br><br>" +
      "Datum: " +
      new Date().toLocaleString();

    setFormState((state) => ({
      ...state,
      isLoading: true,
    }));

    axios({
      method: "post",
      url: "/api/mail",
      data: {
        recipient: recipient || "edvard.mansson@metlab.se",
        html_message: html_message,
      },
    }).then((res) => {
      if (res.status !== 200) {
        setFormState((state) => ({
          ...state,
          isLoading: false,
          error: res.data.error_msg,
        }));
      } else {
        setFormState((state) => ({
          ...state,
          isLoading: false,
          success: true,
        }));
      }
    });
  };

  return (
    <div className="px-6 md:px-0">
      <div
        id="kontakt"
        className="my-10 md:my-24 container mx-auto flex flex-col md:flex-row b-shadow overflow-hidden rounded-lg"
      >
        <div className="relative w-full py-2 md:py-24 bg-metlab-500 md:w-1/2 flex flex-col item-center justify-center">
          <div className="absolute top-0 left-0 z-10 grid-indigo w-16 h-16 md:w-40 md:h-40 md:ml-20 md:mt-24"></div>

          <div className="relative text-2xl md:text-5xl py-2 px-6 md:py-6 md:px-1 md:w-80 md:mx-auto text-metlab-200 font-semibold leading-tight tracking-tight mb-0 z-20 text-center flex flex-col align-center">
            <span>{title}</span>
          </div>
        </div>

        <div className="bg-gray-100 md:w-1/2">
          <div className="flex flex-col h-full relative">
            <div className="absolute top-0 left-0 mt-3 ml-4 md:mt-5 md:ml-12">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="text-metlab-200 fill-current w-12 h-12 md:w-16 md:h-16"
                viewBox="0 0 24 24"
              >
                <path d="M6.5 10c-.223 0-.437.034-.65.065.069-.232.14-.468.254-.68.114-.308.292-.575.469-.844.148-.291.409-.488.601-.737.201-.242.475-.403.692-.604.213-.21.492-.315.714-.463.232-.133.434-.28.65-.35.208-.086.39-.16.539-.222.302-.125.474-.197.474-.197L9.758 4.03c0 0-.218.052-.597.144C8.97 4.222 8.737 4.278 8.472 4.345c-.271.05-.56.187-.882.312C7.272 4.799 6.904 4.895 6.562 5.123c-.344.218-.741.4-1.091.692C5.132 6.116 4.723 6.377 4.421 6.76c-.33.358-.656.734-.909 1.162C3.219 8.33 3.02 8.778 2.81 9.221c-.19.443-.343.896-.468 1.336-.237.882-.343 1.72-.384 2.437-.034.718-.014 1.315.028 1.747.015.204.043.402.063.539.017.109.025.168.025.168l.026-.006C2.535 17.474 4.338 19 6.5 19c2.485 0 4.5-2.015 4.5-4.5S8.985 10 6.5 10zM17.5 10c-.223 0-.437.034-.65.065.069-.232.14-.468.254-.68.114-.308.292-.575.469-.844.148-.291.409-.488.601-.737.201-.242.475-.403.692-.604.213-.21.492-.315.714-.463.232-.133.434-.28.65-.35.208-.086.39-.16.539-.222.302-.125.474-.197.474-.197L20.758 4.03c0 0-.218.052-.597.144-.191.048-.424.104-.689.171-.271.05-.56.187-.882.312-.317.143-.686.238-1.028.467-.344.218-.741.4-1.091.692-.339.301-.748.562-1.05.944-.33.358-.656.734-.909 1.162C14.219 8.33 14.02 8.778 13.81 9.221c-.19.443-.343.896-.468 1.336-.237.882-.343 1.72-.384 2.437-.034.718-.014 1.315.028 1.747.015.204.043.402.063.539.017.109.025.168.025.168l.026-.006C13.535 17.474 15.338 19 17.5 19c2.485 0 4.5-2.015 4.5-4.5S19.985 10 17.5 10z" />
              </svg>
            </div>

            <div className="h-full relative z-10">
              <div className="text-gray-600 serif font-normal italic px-6 py-6 md:px-16 md:py-10 text-xl md:text-2xl">
                <div className="mt-5 md:mt-0 md:col-span-2">
                  <div>
                    {!formState.success ? (
                      <form action="#" method="POST" onSubmit={handleSubmit}>
                        <div className="shadow overflow-hidden sm:rounded-md">
                          <div className="px-4 py-5 bg-white sm:p-6">
                            <div className="grid grid-cols-6 gap-6">
                              <div className="col-span-6 sm:col-span-3">
                                <label
                                  htmlFor="name"
                                  className="block text-base font-medium text-gray-700"
                                >
                                  {nameLabel && nameLabel}*
                                </label>
                                <input
                                  type="text"
                                  name="name"
                                  id="name"
                                  required
                                  autoComplete="given-name"
                                  className="mt-1 px-3 py-4 text-gray-700 focus:ring-metlab-500 focus:border-metlab-500 block w-full shadow-sm sm:text-base border rounded-md"
                                  value={formState.data.name}
                                  onChange={(e) =>
                                    updateFormProperty("name", e.target.value)
                                  }
                                />
                              </div>

                              <div className="col-span-6 sm:col-span-3">
                                <label
                                  htmlFor="company"
                                  className="block text-base font-medium text-gray-700"
                                >
                                  {companyLabel && companyLabel}
                                </label>
                                <input
                                  type="text"
                                  name="company"
                                  id="company"
                                  autoComplete="company"
                                  className="mt-1 px-3 py-4 text-gray-700 focus:ring-metlab-500 focus:border-metlab-500 block w-full shadow-sm sm:text-base border rounded-md"
                                  value={formState.data.company}
                                  onChange={(e) =>
                                    updateFormProperty(
                                      "company",
                                      e.target.value
                                    )
                                  }
                                />
                              </div>

                              <div className="col-span-6 sm:col-span-6">
                                <label
                                  htmlFor="email_address"
                                  className="block text-base font-medium text-gray-700"
                                >
                                  {emailLabel && emailLabel}*
                                </label>
                                <input
                                  type="email"
                                  name="email_address"
                                  id="email_address"
                                  autoComplete="email"
                                  required
                                  className="mt-1 px-3 py-4 text-gray-700 focus:ring-metlab-500 focus:border-metlab-500 block w-full shadow-sm sm:text-base border rounded-md"
                                  value={formState.data.email}
                                  onChange={(e) =>
                                    updateFormProperty("email", e.target.value)
                                  }
                                />
                              </div>

                              <div className="col-span-5 sm:col-span-6">
                                <label
                                  htmlFor="message"
                                  className="block text-base font-medium text-gray-700"
                                >
                                  {messageLabel && messageLabel}*
                                </label>
                                <textarea
                                  name="message"
                                  id="message"
                                  autoComplete="message"
                                  className="w-full px-3 py-4 text-gray-700 border sm:text-base rounded-md focus:outline-none"
                                  value={formState.data.message}
                                  required
                                  onChange={(e) =>
                                    updateFormProperty(
                                      "message",
                                      e.target.value
                                    )
                                  }
                                />
                              </div>
                            </div>
                          </div>
                          <div className="px-4 py-3 bg-gray-50 sm:px-6">
                            <button
                              type="submit"
                              disabled={formState.isLoading}
                              className="inline-flex justify-center py-3 px-6 border border-transparent shadow-sm text-base font-medium rounded-full text-white bg-metlab-500 hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-metlab-500"
                            >
                              {submitLabel && submitLabel}
                            </button>
                            <Link
                              to={termsLink.url || ""}
                              className="text-base text-gray-700 italic block mt-2 hover:text-metlab-500"
                            >
                              {termsLink.title && termsLink.title}
                            </Link>
                          </div>
                        </div>
                      </form>
                    ) : (
                      <div className="text-center">
                        <h2 className="text-2xl font-bold text-gray-700">
                          {successMessage && successMessage}
                        </h2>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;
